/**
 * The runtime half of the middleware port.
 *
 * `route-guard.ts` holds the DECISION (pure, unit-tested against every branch
 * of `middleware.ts`); this component holds the REACTION — redirect, render,
 * or hold. Keeping them apart is what makes SAFE-04 testable: the rule is
 * asserted in `tests/route-guard.test.ts` rather than inferred from JSX.
 *
 * Three states the Next.js middleware never had to model, because it ran on
 * the server with the session and the permission rows already in hand:
 *
 *   1. `status === "loading"` — the persisted session has not been read yet.
 *      Redirecting here would bounce every hard refresh through `/login`.
 *   2. `permissions-unknown` — a USER whose permission map has not arrived.
 *      NOT a denial: a failed fetch must never be presented to a family member
 *      as "you no longer have access". They get an explicit retry instead.
 *   3. `permissionsStatus === "error"` — the same, after the fetch has
 *      actually failed, so the retry button has something to retry.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth/AuthProvider";
import { guardRoute, redirectFor } from "@/lib/rbac/route-guard";
import { FullPageMessage, FullPageSpinner } from "@/components/ui/FullPageState";
import type { ReactNode } from "react";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, role, permissions, permissionsStatus, reloadPermissions } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <FullPageSpinner label="Restoring your session…" />;
  }

  const decision = guardRoute(
    location.pathname,
    status === "authenticated" && role ? { role } : null,
    permissions,
  );

  if (decision.kind === "permissions-unknown") {
    if (permissionsStatus === "error") {
      return (
        <FullPageMessage
          title="Couldn't load your access settings"
          body="Your sign-in worked, but we couldn't read which pages you're allowed. Nothing has changed about your access — this is a connection problem."
          actionLabel="Try again"
          onAction={() => void reloadPermissions()}
        />
      );
    }
    return <FullPageSpinner label="Checking your access…" />;
  }

  const target = redirectFor(decision);
  if (target) {
    // `replace` keeps a denied URL out of the back-stack, matching the
    // middleware's redirect (which the browser never records as a visit).
    return <Navigate to={target} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * The inverse gate for `/login`: a signed-in user who navigates there is sent
 * on to the dashboard, mirroring what the Next.js login page does today.
 */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <FullPageSpinner label="Restoring your session…" />;
  if (status === "authenticated") return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
